/**
 * Attio Import Wizard
 *
 * Three-step fullscreen wizard for importing data from Attio:
 *   1. Connect — paste + validate an API key
 *   2. Configure — choose which object types to import
 *   3. Review — review + override the engine's proposed property mappings
 *
 * Wizard chrome (modal shell, step indicator, footer, dirty-close
 * confirmation) is provided by the base `Wizard` component. On successful
 * create, notifies the parent with the returned import_id so it can hand
 * off to the reused CSV `ReviewModal`.
 *
 * This component itself only wires step rendering to the `Wizard` primitive;
 * wizard state lives in `useAttioWizardState` and the three step
 * onBeforeNext/onConfirm handlers live in `useAttioWizardStepHandlers`.
 */

import { useCallback, useMemo } from 'react';

import { Wizard, WizardPollingBody, type WizardStep } from '@/lib/base/wizard';
import type { CsvImportStatus } from '@/lib/csv-import/api';

import type { AttioMappingAction } from './api';
import { AttioConfigureStep } from './AttioConfigureStep';
import { AttioConnectStep } from './AttioConnectStep';
import { AttioReviewStep } from './AttioReviewStep';
import {
  STEP_CONFIGURE,
  STEP_CONNECT,
  STEP_REVIEW,
} from './attioWizardHelpers';
import { useAttioWizardState } from './useAttioWizardState';
import { useAttioWizardStepHandlers } from './useAttioWizardStepHandlers';

interface AttioImportWizardProps {
  orgId: number;
  isOpen: boolean;
  onClose: () => void;
  onImportCreated: (importId: number, status: CsvImportStatus) => void;
}

export function AttioImportWizard({
  orgId,
  isOpen,
  onClose,
  onImportCreated,
}: AttioImportWizardProps) {
  const state = useAttioWizardState(onClose);
  const { stateRef, handleCancelPoll, handleActionChange } = state;
  const { handleConnectBeforeNext, handleConfigureBeforeNext, handleConfirm } =
    useAttioWizardStepHandlers({ orgId, onImportCreated, state });

  const renderConnect = useCallback(
    () => (
      <AttioConnectStep
        apiKey={stateRef.current.apiKey}
        errorMessage={stateRef.current.connectError}
        onApiKeyChange={state.handleApiKeyChange}
      />
    ),
    [stateRef, state.handleApiKeyChange]
  );

  const renderConfigure = useCallback(
    () => (
      <WizardPollingBody
        title='Analyzing your Attio workspace…'
        description='We’re mapping your Attio fields to Ahoy properties. This can take a few minutes for a large workspace.'
        onCancel={handleCancelPoll}
      >
        <AttioConfigureStep
          selected={stateRef.current.selectedTypes}
          onChange={state.handleTypesChange}
          discoveredTypes={stateRef.current.discoveredTypes}
          standardAvailability={stateRef.current.standardAvailability}
        />
      </WizardPollingBody>
    ),
    [stateRef, state.handleTypesChange, handleCancelPoll]
  );

  const handleReviewActionChange = useCallback(
    (key: string, action: AttioMappingAction) =>
      handleActionChange(key, action),
    [handleActionChange]
  );

  const renderReview = useCallback(
    () => (
      <WizardPollingBody
        title='Starting your import…'
        description='We’re fetching your Attio records and creating the import. This can take a few minutes for a large workspace.'
        onCancel={handleCancelPoll}
      >
        <AttioReviewStep
          proposalsByEntity={stateRef.current.proposalsByEntity}
          loadError={stateRef.current.proposalsLoadError}
          truncatedByEntity={stateRef.current.truncatedByEntity}
          totalRecordsByEntity={stateRef.current.totalRecordsByEntity}
          actionByKey={stateRef.current.actionByKey}
          onActionChange={handleReviewActionChange}
          discoveredTypes={stateRef.current.discoveredTypes}
        />
      </WizardPollingBody>
    ),
    [stateRef, handleReviewActionChange, handleCancelPoll]
  );

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        id: STEP_CONNECT,
        label: 'Connect',
        title: 'Connect Attio',
        render: renderConnect,
        canAdvance: () => stateRef.current.apiKey.trim() !== '',
        onBeforeNext: handleConnectBeforeNext,
        primaryCta: {
          label: ctx => (ctx.isSubmitting ? 'Verifying…' : 'Continue'),
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
      },
      {
        id: STEP_CONFIGURE,
        label: 'Configure',
        title: 'Choose what to import',
        render: renderConfigure,
        canAdvance: () => stateRef.current.selectedTypes.length > 0,
        onBeforeNext: handleConfigureBeforeNext,
        primaryCta: {
          label: ctx => (ctx.isSubmitting ? 'Loading…' : 'Continue'),
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
      },
      {
        id: STEP_REVIEW,
        label: 'Review',
        title: 'Review properties',
        render: renderReview,
        canAdvance: () => true,
        primaryCta: {
          label: ctx =>
            ctx.isSubmitting ? 'Starting import…' : 'Start import',
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
        maxContentWidth: 900,
      },
    ],
    [
      stateRef,
      renderConnect,
      renderConfigure,
      renderReview,
      handleConnectBeforeNext,
      handleConfigureBeforeNext,
    ]
  );

  return (
    <Wizard
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={state.handleOpenChange}
      steps={steps}
      onConfirm={handleConfirm}
      isDirty={state.isDirty}
      dialogAriaLabel='Import from Attio'
      instance='attio-import'
    />
  );
}

export default AttioImportWizard;
